import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, ShoppingCart, ShieldCheck, Clock, Package, Sparkles, Zap } from 'lucide-react';
import { Button } from './ui/Button';
const features = [{
  icon: CalendarDays,
  title: 'Plan paso a paso',
  description: 'Qué hacer cada día, en qué orden y qué observar. Sin adivinar.',
  color: 'from-blue-500 to-cyan-500',
  bgColor: 'bg-blue-50',
  highlight: 'Día a día guiado'
}, {
  icon: ShoppingCart,
  title: 'Recetas',
  description: 'Para copiar, por presupuesto y con sustitutos fáciles de encontrar.',
  color: 'from-emerald-500 to-teal-500',
  bgColor: 'bg-emerald-50',
  highlight: 'Listas para usar'
}, {
  icon: ShieldCheck,
  title: 'Toppers seguros',
  description: 'Cuánto usar, con qué frecuencia y combinaciones simples que funcionan.',
  color: 'from-violet-500 to-purple-500',
  bgColor: 'bg-violet-50',
  highlight: 'Sin riesgos'
}, {
  icon: Clock,
  title: 'Meal prep rápido',
  description: 'Porcionado semanal en 20 minutos, sin ingredientes raros ni complicaciones.',
  color: 'from-amber-500 to-orange-500',
  bgColor: 'bg-amber-50',
  highlight: 'Solo 20 min/semana'
}];
export function WhatYouGetSection() {
  return <section id="que-incluye" className="py-24 bg-gradient-to-b from-[#FAE8FF]/40 via-white to-[#FAE8FF]/40 relative overflow-hidden" data-id="element-273">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D946EF]/10 rounded-full blur-3xl" data-id="element-274" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#F0ABFC]/20 rounded-full blur-3xl" data-id="element-275" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-276">
        {/* Header Section - Centered and More Prominent */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-277">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#D946EF] to-[#A21CAF] text-white text-sm font-bold mb-6 shadow-lg shadow-[#D946EF]/30" data-id="element-278">
            <Package className="w-4 h-4" data-id="element-279" />
            TODO INCLUIDO EN UN SOLO PAQUETE
            <Sparkles className="w-4 h-4" data-id="element-280" />
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2D2D2D] mb-6 leading-tight" data-id="element-281">
            Tu sistema completo
            <br data-id="element-282" />
            <span className="bg-gradient-to-r from-[#D946EF] via-[#E879F9] to-[#A21CAF] bg-clip-text text-transparent" data-id="element-283">
              7 días → 7 mejoras visibles
            </span>
          </h2>

          <p className="text-xl text-[#666666] max-w-2xl mx-auto leading-relaxed" data-id="element-284">
            No es un ebook más. Es un{' '}
            <span className="font-semibold text-[#2D2D2D]" data-id="element-285">
              sistema probado
            </span>{' '}
            con todo lo que necesitas para transformar la alimentación de tu
            Frenchie.
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-12 items-center" data-id="element-286">
          <div className="lg:w-1/2" data-id="element-287">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-288">
              {/* Feature subtitle */}
              <div className="flex items-center gap-2 mb-6" data-id="element-289">
                <Zap className="w-5 h-5 text-[#D946EF]" data-id="element-290" />
                <span className="text-[#A21CAF] font-bold tracking-wide uppercase text-sm" data-id="element-291">
                  Lo que recibes hoy:
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-292">
                {features.map((feature, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className={`${feature.bgColor} p-8 rounded-2xl border-2 border-white shadow-lg relative overflow-hidden group`} data-id="element-293">
                    {/* Gradient accent */}
                    <div className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${feature.color}`} data-id="element-294" />

                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 shadow-lg transform group-hover:scale-110 transition-transform duration-300`} data-id="element-295">
                      <feature.icon className="w-8 h-8 text-white" data-id="element-296" />
                    </div>

                    {/* Highlight badge */}
                    <span className={`inline-block text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full bg-gradient-to-r ${feature.color} text-white mb-3`} data-id="element-297">
                      {feature.highlight}
                    </span>

                    <h3 className="text-xl md:text-2xl font-bold text-[#2D2D2D] mb-3" data-id="element-298">
                      {feature.title}
                    </h3>
                    <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed" data-id="element-299">
                      {feature.description}
                    </p>
                  </motion.div>)}
              </div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5
            }} className="mt-10 flex flex-col sm:flex-row items-center gap-4" data-id="element-300">
                <Button size="lg" href="#oferta" data-id="element-301">
                  <Sparkles className="w-5 h-5 mr-2" data-id="element-302" />
                  Quiero el sistema completo
                </Button>
                <span className="text-sm text-[#666666]" data-id="element-303">
                  ⚡ Acceso inmediato
                </span>
              </motion.div>
            </motion.div>
          </div>

          <div className="lg:w-1/2 relative" data-id="element-304">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative" data-id="element-305">
              {/* Floating badge */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.3
            }} className="absolute -top-4 -right-4 z-20 bg-gradient-to-r from-[#D946EF] to-[#A21CAF] text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg" data-id="element-306">
                🎉 +500 familias felices
              </motion.div>

              <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white" data-id="element-307">
                <img src="https://cdn.magicpatterns.com/uploads/9WZLTgKpogFuxJ7obG9y2z/pasted-image.png" alt="Pareja preparando comida saludable para su French Bulldog" className="w-full h-auto object-cover" data-id="element-308" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D2D2D]/80 via-[#2D2D2D]/20 to-transparent flex items-end p-8" data-id="element-309">
                  <div data-id="element-310">
                    <p className="text-white text-lg font-medium mb-2" data-id="element-311">
                      "Ingredientes frescos, recetas fáciles"
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-312">
                      <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full" data-id="element-313">
                        ✓ Acceso inmediato
                      </span>
                      <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full" data-id="element-314">
                        ✓ Rutina clara
                      </span>
                      <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full" data-id="element-315">
                        ✓ Ingredientes caseros
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}